import If from "@/components/common/If";
import {
  AudioLines,
  ChevronDown,
  HammerIcon,
  Leaf,
  Lock,
  Music,
  ShieldCheck,
  ShowerHead,
} from "lucide-react";

import Link from "next/link";

import { Button } from "@/components/actions/Button";

import Image from "@/components/common/Image";
import { Tooltip } from "@/components/ui/Tooltip";
import RichText from "@/components/common/RichText";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CardProps {
  title: string;
  description: string;
  image?: string;
  className?: string;
  showButton?: boolean;
  href?: string;
  linkText?: string;
  showResources?: boolean;
  isLight?: boolean;
  imageCover?: boolean;
  imageWidth?: number;
  imageHeight?: number;
  showSolutions?: boolean;
  solutions?: any;
  cardType?: string;
  subtitle?: string;
  dropdownLinks?: Array<{ key: string; value: string }>;
}

const Card: React.FC<CardProps> = ({
  title,
  description,
  image,
  className = "",
  showButton = true,
  showSolutions = true,
  href = "",
  linkText = "Learn More",
  showResources = true,
  isLight = false,
  imageCover = true,
  imageWidth,
  imageHeight,
  solutions = [],
  cardType = "Default",
  subtitle = "",
  dropdownLinks = [],
}) => {
  const i = image?.includes("pdf") ? image + "?auto=webp&format=jpeg" : image;

  // A wide image fitted into the tall square box leaves blank space above and below it.
  const fitWideImage =
    !imageCover && cardType !== "Icon" && (imageWidth ?? 0) > (imageHeight ?? 0);
  const imageSize =
    cardType === "Icon"
      ? "h-[80px] aspect-[1/1]"
      : fitWideImage
        ? "h-auto max-h-[50dvh]"
        : "h-[600px] max-h-[50dvh] aspect-[1/1]";

  const imageElement = (
    <Image
      src={i}
      alt={title}
      // className={`w-full object-cover h-[600px] aspect-[1/1] mix-blend-multiply`}
      imgClassName={`w-full ${imageSize}`}
      width={fitWideImage ? String(imageWidth) : ""}
      height={fitWideImage ? String(imageHeight) : ""}
      cover={imageCover}
      maxWidth="1000"
    />
  );

  return (
    <article
      className={`flex-card ${!imageCover ? "px-[2rem]" : ""} ${className} stack`}
    >
      <If condition={image}>
        {href ? <Link href={href}>{imageElement}</Link> : imageElement}
      </If>
      <If condition={!image}>
        <div className="bg-gray-200 aspect-[1/1] w-full mb-4"></div>
      </If>
      <div
        className={`py-4 stack gap-6 flex-1 justify-between ${!imageCover ? "" : "px-[2rem]"}`}
      >
        <div className="stack gap-6">
          <div className="stack gap-3 justify-between">
            <If condition={showSolutions}>
              <div className="flex gap-4 opacity-40">
                <If condition={solutions?.includes("Durability")}>
                  <Link href="/solutions/durability">
                    <Tooltip text="Durability">
                      <HammerIcon size={24} />
                    </Tooltip>
                  </Link>
                </If>
                <If condition={solutions?.includes("Safety")}>
                  <Link href="/solutions/safety">
                    <Tooltip text="Safety">
                      <ShieldCheck size={24} />
                    </Tooltip>
                  </Link>
                </If>
                <If condition={solutions?.includes("Security")}>
                  <Link href="/solutions/security">
                    <Tooltip text="Security">
                      <Lock size={24} />
                    </Tooltip>
                  </Link>
                </If>
                <If condition={solutions?.includes("Acoustics")}>
                  <Link href="/solutions/acoustics">
                    <Tooltip text="Acoustics">
                      <AudioLines size={24} />
                    </Tooltip>
                  </Link>
                </If>
                <If condition={solutions?.includes("Cleanability")}>
                  <Link href="/solutions/cleanability">
                    <Tooltip text="Cleanability">
                      <ShowerHead size={24} />
                    </Tooltip>
                  </Link>
                </If>
              </div>
            </If>
            <If condition={subtitle}>
              <h3
                className="text-sm text-gray"
                dangerouslySetInnerHTML={{
                  __html: subtitle
                    ?.replace("Aspiro", "Aspiro<sup>&reg;</sup>")
                    ?.replace("Cendura", "Cendura<sup>&reg;</sup>"),
                }}
              />
            </If>
            <h2
              className={`text-[2rem] font-normal ${cardType === "Icon" ? "text-center" : ""}`}
            >
              {href ? (
                <Link href={href} className="text-current hover:text-current">
                  {title}
                </Link>
              ) : (
                title
              )}
            </h2>
            <If condition={cardType !== "Icon"}>
              <div className="bg-gold h-[5px] w-[35px]" />
            </If>
          </div>
          <If condition={description?.json}>
            <div
              className={`text-gray-600 min-h-[4em]  overflow-hidden text-body ${cardType === "Icon" ? "text-center" : ""}`}
            >
              <If condition={description?.json}>
                <RichText json={description?.json} />
              </If>

              <If condition={!description?.json}>
                <p></p>
              </If>
            </div>
          </If>
        </div>
        <If condition={showButton && href}>
          <div className="flex stack gap-8">
            <Button href={href} isLight={isLight}>
              {linkText || "Learn More"}
            </Button>
          </div>
        </If>
        <If condition={dropdownLinks?.length > 0}>
          <div className="px-2 pb-4">
            <Select onValueChange={(value) => window.open(value, "_blank")}>
              <SelectTrigger className="bg-transparent border-current text-current focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                <SelectValue placeholder="Select an option..." />
                <ChevronDown className="h-4 w-4 text-gold ml-auto" />
              </SelectTrigger>
              <SelectContent>
                {dropdownLinks.map((link, index) => (
                  <SelectItem key={index} value={link.value}>
                    {link.key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </If>
      </div>
    </article>
  );
};

export default Card;
